import React, { FC } from 'react';
import { useMutation } from '@apollo/client';
import {
	alephToast, NotificationToast, NotificationType, isReddit, isSpotify,
	Avatar, getAvatarSource,
} from '@aleph/shared-components';
import { AgenyAccessOptionType } from 'wb-reddit/app/routes/advertisers/utils';
import {
	CancelAgencyInvitationDocument,
	RedditAdvertiserListDocument,
	RevokeGroupAccessToAgencyDocument,
	RevokeUserAccessToAgencyDocument,
	UserGroup,
	UserGroupRole,
} from 'wb-reddit/graphql/generated';
import {
	AuthGraphRevokeAdvertiserAccountAccessDocument,
	UserGroupAccessRole,
	UserGroup as AuthUserGroup,
	User as AuthUser,
} from 'wb-spotify/graphql/authgraph/generated';
import useRedditAdvertiserMutator from 'wb-reddit/app/routes/advertisers/hooks/useRedditAdvertiserMutator';
import AccessListItemRevoke from 'wb-reddit/src/app/routes/advertisers/mutator/accessListItemRevoke';
import { useTranslation } from 'react-i18next';
import { mapTypeNameToAvatarEntity } from 'wb-reddit/app/common/utils';
import { getUserInitials } from 'wb-reddit/app/routes/users/utils';

type AgencyAccessListItemProps = {
	entity: AgenyAccessOptionType & { you?: boolean, isOwner?: boolean };
	onRemove?: () => void;
	disabled?: boolean
};

const getDomain = (entity: AgenyAccessOptionType) => {
	if (isReddit()) {
		switch (entity?.__typename) {
			case 'SearchUser':
				return entity?.userEmail?.split('@')?.[1];
			case 'AgencyUser':
				return entity?.user?.userEmail?.split('@')?.[1];
			case 'UserGroup':
				return (entity as UserGroup)?.users
					?.find(user => user.userGroupUserRole === UserGroupRole.Owner)
					?.user?.userEmail?.split('@')?.[1];
			default:
				return 'Unknown';
		}
	}

	switch (entity?.__typename) {
		case 'User':
			return entity?.userEmail?.split('@')?.[1];
		case 'UserGroup':
			return (entity as AuthUserGroup)?.userGroupAccess
				?.find(user => user?.userGroupAccessRole === UserGroupAccessRole.Owner)
				?.user?.userEmail?.split('@')?.[1];
		default:
			return 'Unknown';
	}
};

function getEntityName(entity: AgenyAccessOptionType) {
	if (isReddit()) {
		switch (entity?.__typename) {
			case 'AgencyUser':
				return `${entity?.user?.userName} ${entity?.user?.userSurname || ''}`;
			case 'UserGroup':
				return entity.userGroupName;
			case 'SearchUser':
				return entity.userFullName;
			default:
				return '-';
		}
	}

	switch (entity?.__typename) {
		case 'User':
			return `${(entity as AuthUser)?.userName} ${(entity as AuthUser).userSurname || ''}`;
		case 'UserGroup':
			return entity?.userGroupName;
		default:
			return '-';
	}
}

const AgencyAccessListItem: FC<AgencyAccessListItemProps> = ({ entity, onRemove, disabled }) => {
	const { t } = useTranslation();
	const { mutatingAdvertiser } = useRedditAdvertiserMutator();
	const isUpdate = mutatingAdvertiser?.agencyAccountId;

	const [revokeUserAccess, { loading: revokingUser }] = useMutation(RevokeUserAccessToAgencyDocument, {
		errorPolicy: 'all',
	});

	const [revokeGroupAccess, { loading: revokingGroup }] = useMutation(RevokeGroupAccessToAgencyDocument, {
		errorPolicy: 'all',
	});

	const [cancelInvitation, { loading: cancellingInvitation }] = useMutation(CancelAgencyInvitationDocument, {
		errorPolicy:         'all',
		refetchQueries:      [RedditAdvertiserListDocument],
		awaitRefetchQueries: true,
	});

	const [revokeAuthGroupAccess, { loading: authrevokingGroup }] = useMutation(AuthGraphRevokeAdvertiserAccountAccessDocument, {
		errorPolicy: 'all',
	});

	const isRevoking = revokingUser || revokingGroup || cancellingInvitation || authrevokingGroup;

	if (!entity) {
		return null;
	}

	const onRevokeAccess = async () => {
		if (isRevoking) {
			return;
		}

		if (!isUpdate) {
			onRemove?.();

			return;
		}

		if (isSpotify()) {
			const { errors } = await revokeAuthGroupAccess({
				variables: {
					advertiserAccountId: mutatingAdvertiser?.id ?? '',
					// @ts-ignore TODO
					userGroupId:         entity.userGroupId,
					appId:               import.meta.env.VITE_APP_ID,
				},
			});

			if (errors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('sc: action err', { action: 'revoking access' })}
					/>,
				);

				return;
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={t('re: advertiser.revoke.success', 'Access successfully revoked')}
				/>,
			);

			onRemove?.();

			return;
		}

		if (entity.__typename === 'AgencyUser') {
			const { errors } = entity?.agencyPendingInviteHash
				? await cancelInvitation({
					variables: {
						hash: entity?.agencyPendingInviteHash,
					},
				})
				: await revokeUserAccess({
					variables: {
						agencyAccountId: mutatingAdvertiser?.agencyAccountId ?? '',
						userIds:         [entity?.user?.userId],
					},
				});

			if (errors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('sc: action err', { action: 'revoking access' })}
					/>,
				);

				return;
			}
		}

		if (entity.__typename === 'UserGroup') {
			const { errors } = await revokeGroupAccess({
				variables: {
					agencyAccountId: mutatingAdvertiser?.agencyAccountId ?? '',
					userGroupIds:    [entity.userGroupId],
				},
			});

			if (errors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('sc: action err', { action: 'revoking access' })}
					/>,
				);

				return;
			}
		}

		alephToast(
			<NotificationToast
				type={NotificationType.Success}
				title={t('re: advertiser.revoke.success', 'Access successfully revoked')}
			/>,
		);

		onRemove?.();
	};

	const domain = getDomain(entity);

	return (
		<div
			className="flex h-[48px] items-center px-[8px] text-[12px] border-b border-b-gray-200"
		>
			<div className="flex-1 text-[16px] font-medium whitespace-nowrap
				items-center text-black flex gap-[8px]"
			>
				<Avatar
					src={getAvatarSource(entity?.__typename === 'AgencyUser' ? entity?.user : entity as Partial<UserGroup>)}
					entityType={mapTypeNameToAvatarEntity(entity?.__typename === 'AgencyUser' ? entity?.user : entity as Partial<UserGroup>)}
					entityUserInitials={getUserInitials(entity?.__typename === 'AgencyUser' ? entity?.user : entity as Partial<UserGroup>)}
				/>
				<div />

				<div className="overflow-hidden overflow-ellipsis">
					{getEntityName(entity)}
				</div>

				{ entity?.__typename === 'AgencyUser'
					&& entity?.agencyPendingInviteHash
					&& <div className="text-gray-700 text-[12px]">{t('re: advertiser.status.pending', 'Pending')}</div> }
			</div>
			<div className="px-[8px] text-gray-700">@{domain}</div>
			{
				!disabled && (
					<AccessListItemRevoke
						isCurrentUser={entity.you}
						isOwner={entity.isOwner}
						loading={isRevoking}
						onClick={onRevokeAccess}
					/>
				)
			}
		</div>
	);
};

export default AgencyAccessListItem;
